import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit3, Calendar, UserCheck, Mail, Phone } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useLanguage } from '@/hooks/use-language';
import { EditProfileDialog } from './EditProfileDialog';

export function ProfileHeader() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const needsProfileCompletion = !user.phone || !user.phone.trim();

  React.useEffect(() => {
    if (needsProfileCompletion) {
      setShowEditDialog(true);
    }
  }, [needsProfileCompletion]);

  if (!user) return null;

  // Generate user initials for avatar
  const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || 'M';
  const avatarSrc = user.avatarUrl && user.avatarUrl.length > 0 ? user.avatarUrl : undefined;

  const memberSince = new Date(user.createdAt).getFullYear();
  const daysSinceJoined = Math.floor((Date.now() - user.createdAt.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <>
      <Card className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-accent/5 border-none shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent" />
        <CardContent className="relative p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Avatar Section */}
            <div className="relative">
              <Avatar className="h-24 w-24 ring-4 ring-primary/20">
                {avatarSrc && (
                  <AvatarImage src={avatarSrc} alt={user.firstName} />
                )}
                <AvatarFallback className="text-xl font-bold bg-primary text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {user.isAdmin && (
                <Badge className="absolute -bottom-2 -right-2 bg-admin-role text-admin-role-foreground">
                  Admin
                </Badge>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    {user.firstName} {user.lastName}
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    {language === 'no' ? 'Medlem siden' : 'Member since'} {memberSince}
                  </p>
                  <div className="mt-3 flex flex-col gap-2 text-sm text-muted-foreground">
                    {user.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>{user.email}</span>
                      </div>
                    )}
                    {user.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span>{user.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEditDialog(true)}
                  className="w-fit"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  {language === 'no' ? 'Rediger' : 'Edit Profile'}
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {language === 'no' ? 'Medlem i' : 'Member for'} {daysSinceJoined} {language === 'no' ? 'dager' : 'days'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <UserCheck className="h-4 w-4" />
                  <span>
                    {language === 'no' ? 'Grupper' : 'Groups'}: {user.groupIds?.length ?? 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <EditProfileDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        user={user}
        forceCompletion={needsProfileCompletion}
      />
    </>
  );
}
